import {getUserCustomFields} from '@Libraries/add-mailing-list-library';
import {
  addUserCustomFieldMeta,
  initEmptyCustomFieldMeta,
  setIsLoadingUserCustomFields,
  updateUserCustomFieldMeta,
  updateUserCustomFieldValue,
} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience-reducer';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {RootState} from '@/store';
import type {PromiseVoidFunction} from '@/types/global';
import type {AudienceCustomFieldData, UserCustomFieldMeta} from '@/components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';

export interface UseUserCustomFields {
  populateUserCustomFields: PromiseVoidFunction;
  customFields: UserCustomFieldMeta[];
  updateCustomFieldMeta: (iduserHasCustomField: number, customFieldMeta: UserCustomFieldMeta) => void;
  updateCustomFieldValue: (audienceId: number, customFieldId: number, value: string) => void;
  getCustomFieldValue: (audienceId: number, customFieldId: number) => string | null;
  doCustomFieldsExist: boolean;
  areLoaded: boolean;
}

export function useUserCustomFields(): UseUserCustomFields {
  const dispatch = useAppDispatch();
  const emailMarketingCampaignAudience = useAppSelector((state: RootState) => {
    return state.emailMarketingCampaignAudience;
  });
  const userCustomFieldMetaMap = emailMarketingCampaignAudience.userCustomFieldsMetaMap;
  const isLoadingUserCustomFields = emailMarketingCampaignAudience.isLoadingUserCustomFields;
  const {audiences} = emailMarketingCampaignAudience;
  const hasFetchedBefore = userCustomFieldMetaMap !== null;
  const doCustomFieldsExist = hasFetchedBefore && !!Object.keys(userCustomFieldMetaMap).length;

  const populateUserCustomFields = async (): Promise<void> => {
    if (isLoadingUserCustomFields) {
      return;
    }

    dispatch(setIsLoadingUserCustomFields(true));
    const customFields = await getUserCustomFields();

    if (!Object.keys(customFields).length) {
      dispatch(initEmptyCustomFieldMeta());
      return;
    }

    Object.values(customFields).forEach((customFieldMeta) => {
      dispatch(
        addUserCustomFieldMeta({
          userCustomFieldId: customFieldMeta.idUserHasCustomField,
          userCustomFieldMeta: customFieldMeta,
        })
      );
    });
    dispatch(setIsLoadingUserCustomFields(false));
  };

  const getCustomFieldForAudience = (audienceId: number): AudienceCustomFieldData | null => {
    return audiences[audienceId].customFields ?? null;
  };

  const getCustomFieldValue = (audienceId: number, customFieldId: number): string | null => {
    const cf = getCustomFieldForAudience(audienceId);
    if (!cf) return null;
    return cf[customFieldId];
  };

  const updateCustomFieldMeta = (iduserHasCustomField: number, customFieldMeta: UserCustomFieldMeta): void => {
    dispatch(
      updateUserCustomFieldMeta({
        userCustomFieldId: iduserHasCustomField,
        userCustomFieldMeta: customFieldMeta,
      })
    );
  };

  const updateUserCustomFieldValueInRedux = (audienceId: number, customFieldId: number, value: string): void => {
    dispatch(
      updateUserCustomFieldValue({
        audienceId,
        userCustomFieldId: customFieldId,
        value,
      })
    );
  };

  return {
    areLoaded: hasFetchedBefore,
    doCustomFieldsExist,
    populateUserCustomFields,
    customFields: userCustomFieldMetaMap ? Object.values(userCustomFieldMetaMap) : [],
    updateCustomFieldMeta,
    updateCustomFieldValue: updateUserCustomFieldValueInRedux,
    getCustomFieldValue,
  };
}
